import { useEffect, useRef, useState } from "react";
import { Button, PageHead } from "../../components/ui";
import { feed, GPS_PARAMS, newTracker, type Tracker, type Verdict } from "../../lib/gps";
import { isApp, platform } from "../../lib/platform";
import { t } from "../../i18n";

/**
 * v0.16.0 (B1) — protótipo técnico de diagnóstico do GPS. Só lê: nada é gravado, enviado, registrado em log nem
 * mostrado como coordenada (apenas precisão, contagens e distância). A leitura começa num clique e o watch é
 * encerrado ao parar ou sair da página. Não é o Modo Corrida (B3+).
 */
type Perm = "unknown" | "granted" | "prompt" | "denied" | "unsupported";

export function GpsDiagnosticPage() {
  const [perm, setPerm] = useState<Perm>("unknown");
  const [running, setRunning] = useState(false);
  const [tr, setTr] = useState<Tracker>(newTracker());
  const [acc, setAcc] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [verdicts, setVerdicts] = useState<Partial<Record<Verdict, number>>>({});
  const [ref, setRef] = useState("");
  const [report, setReport] = useState("");
  const accs = useRef<number[]>([]);
  const t0 = useRef<number>(0);
  const watch = useRef<number | null>(null);
  const trRef = useRef<Tracker>(tr);
  const verdictsRef = useRef<Partial<Record<Verdict, number>>>({});

  const secure = typeof window !== "undefined" && window.isSecureContext;
  const hasGeo = typeof navigator !== "undefined" && "geolocation" in navigator;
  const touch = typeof matchMedia !== "undefined" && matchMedia("(pointer: coarse)").matches;

  useEffect(() => {
    if (!navigator.permissions?.query) return;
    navigator.permissions.query({ name: "geolocation" as PermissionName }).then(
      (s) => { setPerm(s.state as Perm); s.onchange = () => setPerm(s.state as Perm); },
      () => setPerm("unknown"),
    );
  }, []);

  const stop = () => {
    if (watch.current !== null) navigator.geolocation.clearWatch(watch.current);
    watch.current = null;
    setRunning(false);
    setReport(buildReport());
  };

  /** Resumo para calibração: contagens, precisão e distância. NÃO inclui coordenadas nem identificação do usuário. */
  const buildReport = () => {
    const a = accs.current.slice().sort((x, y) => x - y);
    const q = (f: number) => (a.length ? Math.round(a[Math.min(a.length - 1, Math.floor(a.length * f))] * 10) / 10 : null);
    const refM = Number(ref.replace(",", "."));
    const uad = (navigator as unknown as { userAgentData?: { mobile?: boolean; platform?: string } }).userAgentData;
    return JSON.stringify({
      versao: "0.16.5", ambiente: isApp ? `app-${platform}` : "navegador", agente: navigator.userAgent.slice(0, 160),
      plataforma: uad?.platform ?? null, movel: uad?.mobile ?? null, toque: touch, permissao: perm,
      wake_lock: "wakeLock" in navigator, segundos: Math.round((Date.now() - t0.current) / 1000),
      leituras: a.length, aceitas: trRef.current.accepted, descartadas: trRef.current.rejected, veredictos: verdictsRef.current,
      precisao_m: { p50: q(0.5), p90: q(0.9), max: a.length ? a[a.length - 1] : null },
      distancia_filtrada_m: Math.round(trRef.current.distanceM), distancia_referencia_m: Number.isFinite(refM) && refM > 0 ? refM : null,
      erro_pct: Number.isFinite(refM) && refM > 0 ? Math.round(((trRef.current.distanceM - refM) / refM) * 1000) / 10 : null,
    }, null, 2);
  };
  useEffect(() => () => { if (watch.current !== null) navigator.geolocation.clearWatch(watch.current); }, []);

  useEffect(() => { if (report) setReport(buildReport()); }, [ref]);   // eslint-disable-line react-hooks/exhaustive-deps

  const start = () => {
    if (!hasGeo) { setPerm("unsupported"); return; }
    setErr(null);
    trRef.current = newTracker();
    setTr(trRef.current);
    verdictsRef.current = {};
    setVerdicts({});
    setReport("");
    accs.current = [];
    t0.current = Date.now();
    setRunning(true);
    watch.current = navigator.geolocation.watchPosition(
      (p) => {
        const r = feed(trRef.current, { lat: p.coords.latitude, lon: p.coords.longitude, accuracy: p.coords.accuracy, ts: p.timestamp });
        trRef.current = r.next;
        setTr(r.next);
        setAcc(p.coords.accuracy);
        if (accs.current.length < 20000) accs.current.push(p.coords.accuracy);
        setErr(null);
        verdictsRef.current = { ...verdictsRef.current, [r.verdict]: (verdictsRef.current[r.verdict] ?? 0) + 1 };
        setVerdicts(verdictsRef.current);
      },
      (e) => {
        setErr(e.code === 1 ? "denied" : e.code === 2 ? "unavailable" : "timeout");
        if (e.code === 1) { setPerm("denied"); stop(); }
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 },
    );
  };

  const rows: [string, string][] = [
    [t("Contexto seguro (HTTPS)"), secure ? t("sim") : t("não — o GPS não funciona")],
    [t("API de geolocalização"), hasGeo ? t("disponível") : t("indisponível")],
    [t("Permissão"), perm],
    [t("Ambiente"), isApp ? `${t("app")} (${platform})` : t("navegador")],
    [t("Tela de toque"), touch ? t("sim") : t("não")],
    [t("Precisão da última leitura"), acc === null ? "—" : `${Math.round(acc)} m`],
    [t("Leituras aceitas / descartadas"), `${tr.accepted} / ${tr.rejected}`],
    [t("Distância (filtrada)"), `${tr.distanceM.toFixed(0)} m`],
    [t("Limite de precisão aceito"), `${GPS_PARAMS.maxAccuracyM} m`],
  ];

  return (
    <>
      <PageHead title={t("Diagnóstico do GPS")} subtitle={t("Teste técnico: nada é gravado nem enviado, e a posição não é exibida.")} />
      <div className="card" style={{ padding: 16, maxWidth: 560 }}>
        <p className="muted">{t("Toque em iniciar e caminhe um pouco com a tela ligada. A leitura é encerrada ao parar ou sair desta página.")}</p>
        <div style={{ display: "flex", gap: 8, margin: "12px 0" }}>
          {!running
            ? <Button variant="primary" onClick={start} disabled={!hasGeo || !secure}>{t("Iniciar leitura")}</Button>
            : <Button variant="secondary" onClick={stop}>{t("Parar")}</Button>}
        </div>
        {err && <p role="alert">{err === "denied" ? t("Permissão negada. Libere a localização nas configurações do navegador.") : err === "unavailable" ? t("Posição indisponível no momento.") : t("Tempo esgotado sem sinal de GPS.")}</p>}
        <table className="table"><tbody>{rows.map(([k, v]) => <tr key={k}><th scope="row" style={{ textAlign: "left" }}>{k}</th><td>{v}</td></tr>)}</tbody></table>
        <label style={{ display: "grid", gap: 4, margin: "8px 0" }}>{t("Distância de referência em metros (do relógio ou de um percurso conhecido)")}
          <input inputMode="decimal" value={ref} onChange={(e) => setRef(e.target.value)} className="input" />
        </label>
        {report && (
          <div>
            <p className="muted">{t("Resultado sem coordenadas. Copie e envie para a calibração.")}</p>
            <textarea readOnly value={report} rows={14} style={{ width: "100%", fontFamily: "monospace", fontSize: 12 }} aria-label={t("Resultado do diagnóstico")} />
            <Button onClick={() => { void navigator.clipboard?.writeText(report).catch(() => undefined); }}>{t("Copiar resultado")}</Button>
          </div>
        )}
        {Object.keys(verdicts).length > 0 && <p className="muted" style={{ marginTop: 8 }}>{Object.entries(verdicts).map(([k, n]) => `${k}: ${n}`).join(" · ")}</p>}
      </div>
    </>
  );
}
